// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Component, OnInit } from '@angular/core';
import { HeaderService } from '../../../header.service';
import { HeaderConstants, IEncryptionHeaderResponse, IFileStatus } from '../../../../types/encryption-header';
import { EncryptionService } from '../../../../services/encryption.service';
import {
  arrayBufferToBase64,
  base64ToArrayBuffer,
  decodeGSM,
  importSecretKeyGCM,
  pushFileToDesktop
} from '../encryption_util';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ActionService } from '../../../../services/action-service';
import { D3eService } from '../../../../services/d3e-service';
import { IDecodedAuthToken } from 'src/types/auth';
import { EventsService } from 'src/services/events-service';
import { EVENT_TYPES } from 'src/types/events';
import { HeaderInfoComponent, IHeaderInfo} from '../../../dialogs/header-info/header-info.component';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import authHelper from '../../../auth/authHelper';
import { GetPasswordComponent } from '../../../dialogs/get-password/get-password.component';
import {TranslateService} from '@ngx-translate/core';

@Component({
    selector: 'app-file-decrypt',
    templateUrl: './file-decrypt.component.html',
    styleUrls: ['./file-decrypt.component.scss'],
    standalone: false
})

export class FileDecryptComponent implements OnInit {
  files: any[] = [];
  filenames = [];
  fileContents = [];
  decryptedFile = [];
  fileStatus: IFileStatus[] = [];
  ready = [];
  username;
  downloadsAvailable = false;
  decodedToken;
  keySize = 256;
  iterations = 100;
  allowShowHeader = false;
  private passwordDialogRef: MatDialogRef<GetPasswordComponent, any>;
  private passwords: string [] = [];
  styles = {
    'border': 'solid 1px rgba(0, 0, 0, 0.3)'
  }
  draggingOver = false;

  fDownload: string;
  fDrag: string;
  fOr: string;
  fBrowse: string;
  fDecryptDL: string;
  fDLAll: string;
  fClear: string;
  fAdditionalInfoRequired: string;
  fPwdInfo: string;
  fPwdEnter: string;
  fMsgPreparing: string;
  fMsgDecrypting: string;
  fMsgSuccess: string;
  fMsgNoAccess: string;
  fMsgFailFile: string;
  fMsgReading: string;
  fMsgLookingHeaders: string;
  fMsgNotD3e: string;
  fMsgProcessing: string;
  fMsgDownloadReady: string;
  fMsgDecrypted: string;
  fMsgFailed: string;

  title: string;
  isSecureContext: boolean;

  constructor(
      private actionService: ActionService,
      private d3eService: D3eService,
      private headerService: HeaderService,
      private encryptionService: EncryptionService,
      private jwtHelper: JwtHelperService,
      private eventsService: EventsService,
      public dialog: MatDialog,
      private translate: TranslateService
  ) {
    this.translate.get(['FD_HEADER', 'FD_NOTE', 'FD_DOWNLOAD', 'FD_DRAGA', 'FD_OR',
      'FD_BROWSE', 'FD_DECRYPT_DL', 'FD_DL_ALL', 'FD_CLEAR', 'TITLE_DECRYPTION', 'FD_PWD_ADDITIONAL', 'FD_PWD_INFO',
        'FD_PWD_ENTER', 'FD_MSG_PREPARING', 'FD_MSG_DECRYPTING', 'FD_MSG_SUCCESS', 'FD_MSG_NO_ACCESS',
        'FD_MSG_FAIL_FILE', 'FD_MSG_READING', 'FD_MSG_LOOKING_HEADERS', 'FD_MSG_NOT_D3E', 'FD_MSG_PROCESSING',
        'FD_MSG_DOWNLOAD_READY', 'FD_MSG_DECRYPTED', 'FD_MSG_FAILED',
        ]
    ).subscribe(vals => {
      this.fDownload = vals['FD_DOWNLOAD'];
      this.fDrag = vals['FD_DRAGA'];
      this.fBrowse = vals['FD_BROWSE'];
      this.fDecryptDL = vals['FD_DECRYPT_DL'];
      this.fDLAll = vals['FD_DL_ALL'];
      this.fClear = vals['FD_CLEAR'];

      this.title = vals['TITLE_DECRYPTION'];
      this.fAdditionalInfoRequired = vals['FD_PWD_ADDITIONAL'];
      this.fPwdInfo = vals['FD_PWD_INFO'];
      this.fPwdEnter = vals['FD_PWD_ENTER'];
      this.fMsgPreparing = vals['FD_MSG_PREPARING'];
      this.fMsgDecrypting = vals['FD_MSG_DECRYPTING'];
      this.fMsgSuccess = vals['FD_MSG_SUCCESS'];
      this.fMsgNoAccess = vals['FD_MSG_NO_ACCESS'];
      this.fMsgFailFile = vals['FD_MSG_FAIL_FILE'];
      this.fMsgReading = vals['FD_MSG_READING'];
      this.fMsgLookingHeaders = vals['FD_MSG_LOOKING_HEADERS'];
      this.fMsgNotD3e = vals['FD_MSG_NOT_D3E'];
      this.fMsgProcessing = vals['FD_MSG_PROCESSING'];
      this.fMsgDownloadReady = vals['FD_MSG_DOWNLOAD_READY'];
      this.fMsgDecrypted = vals['FD_MSG_DECRYPTED'];
      this.fMsgFailed = vals['FD_MSG_FAILED'];

    });
    this.headerService.setTitle(this.title);
    this.headerService.setSubtitle('');
    this.headerService.setIcon('mdi-zip-box-outline');
    this.headerService.setNavMenuText('');
    this.headerService.showUserFilter(false);

    this.isSecureContext = window.isSecureContext;
  }

  ngOnInit(): void {
    const token = this.jwtHelper.tokenGetter();
    try {
        this.decodedToken = this.jwtHelper.decodeToken<IDecodedAuthToken>(token as string);
        this.username = this.decodedToken.usr;
    } catch (error) {
        this.decodedToken = null;
    }
    this.allowShowHeader = authHelper.getFeatureFlagFromToken('adp') || false;
  }

  /**
   * on file drop handler
   */
  onFileDropped(dropevent) {
    this.prepareFilesList(dropevent);
    this.styles = {
      'border': 'solid 1px rgba(0, 0, 0, 0.3)'
    };
    this.draggingOver = false;
  }

  onDragOver() {
    this.styles = {
      'border': 'dashed 5px rgba(0, 0, 0, 0.17)'
    };
    this.draggingOver = true;
  }

  onDragLeave() {
    this.styles = {
      'border': 'solid 1px rgba(0, 0, 0, 0.3)'
    };
    this.draggingOver = false;
  }
  /**
   * handle file from browsing
   */
  fileBrowseHandler(filesevent) {
    this.prepareFilesList(filesevent);
  }

  formatBytes(size: any) {
    // Intentionally empty placeholder for formatting overrides
  }

  download(i: number) {
    // Intentionally empty placeholder for formatting overrides
  }

    /**
   * Convert Files list to normal array list
   * @param files (Files List)
   */
  prepareFilesList(files: Array<any>) {
    for (const theFile of files) {
      theFile.progress = 0;
      this.files.push(theFile);
      // change the name of the file.  remove the .d3e extennsion
      const filename = theFile.name.replace('.d3e', '');
      this.filenames.push(filename);

      const index = this.files.length - 1;
      const status = new IFileStatus();
      status.message = ' ... ';
      status.bytesRequired = HeaderConstants.HEADER_INFO_SIZE;

      this.fileStatus[index] = status;
      this.ready[index] = false;
      this.passwords = [];

      setTimeout( function(fileDecryptComponent, arrayIndex) {
        fileDecryptComponent.load_file(arrayIndex);
      }, 0, this, index);
    }
    // this.fileDropEl.nativeElement.value = "";
  }

  async load_file(index) {
    const file = this.files[index];
    const fileContent: ArrayBuffer = await this.readFileContent(file, index);
    this.fileContents[index] = fileContent;
    this.decryptHeader(index, HeaderConstants.HEADER_INFO_SIZE);
  }

  getHeaderSize(response: IEncryptionHeaderResponse) {
    return response.headerSize;
  }

  decryptHeader(index, headerSize, password_index = 0, current_password: string = null) {
      const slice = this.fileContents[index].slice(0, headerSize);
      if (slice.byteLength < headerSize) {
        this.failure(index, this.fMsgNotD3e);
        return;
      }
      const base64HeaderString = arrayBufferToBase64(slice);
      const filename = this.filenames[index];

      this.encryptionService.decryptHeader(base64HeaderString, current_password, filename).subscribe( response => {
          this.fileStatus[index].message += ' . ';
          if ( response.needMore ) {
            const bytes_required = this.getHeaderSize(response);
            this.fileStatus[index].bytesRequired = bytes_required;
            this.decryptHeader(index, bytes_required);
          } else if ( response.needsPassword ) {
            const bytes_required = this.getHeaderSize(response);
            this.fileStatus[index].bytesRequired = bytes_required;
            if (password_index < this.passwords.length) {
              const password: string = this.passwords[password_index];
              password_index++;
              this.decryptHeader(index, bytes_required, password_index, password);
            } else {
              // need to request a password from user and store in passwords list
              if ( this.passwords.length > HeaderConstants.MAX_PASSWORDS )  {
                this.failure(index, 'Maximum passwords attempted.');
                return;
              } else {
                // why are we doing this
                // When putting up more than one file at a time, we may need to ask for passwords, and we want the passwords
                // to apply to all.  if the screen is asking for a password, then allow it to finish, and grab it and continue.
                if ( this.passwordDialogRef === undefined || this.passwordDialogRef.getState() === MatDialogState.CLOSED ) {
                  this.passwordDialogRef = this.dialog.open(GetPasswordComponent, {
                    width: '700',
                    height: 'auto',
                    data: {
                      title: this.fAdditionalInfoRequired,
                      info: this.fPwdInfo,
                      fieldLabel: this.fPwdEnter
                    },
                  });
                }
                this.passwordDialogRef.afterClosed().subscribe(newPassword => {
                  if (newPassword !== undefined && newPassword != null && newPassword.length > 0) {
                    let password;
                    if (password_index < this.passwords.length) {
                      // get the next password that was added by another request.
                      password = this.passwords[password_index];
                    } else {
                      // add the password to the list
                      this.passwords.push(newPassword);
                      password = this.passwords[password_index];
                      password_index++;
                    }
                    this.decryptHeader(index, bytes_required, password_index, password);
                  } else {
                    this.failure(index, 'Password was not supplied');
                  }
                });
              }
            }
          } else {
            if ( response.isD3E ) {
              if ( response.symmetricKey !== undefined ) {
                this.fileStatus[index].message = this.fMsgDecrypting;
                this.success(index, this.fMsgSuccess);
                this.decrypt(index, response);
              } else {
                this.failure(index);
              }
            } else {
              this.failure(index, this.fMsgNotD3e);
            }
          }
      }, error => {
        this.failure(index);
      });
  }

  failure(index, errorMessage = this.fMsgNoAccess, event_type = EVENT_TYPES.DECRYPT_HEADER) {
    this.fileStatus[index].error = true;
    this.fileStatus[index].message = errorMessage;
    this.fileStatus[index].headerProcessed = true;
    const type = event_type;
    const description = errorMessage;
    const detail = this.fMsgFailFile + this.filenames[index];
    this.verifyHeadersComplete();
    this.eventsService.error({type, description, detail}).subscribe();
  }

  private verifyHeadersComplete() {
    const stillWorking = this.fileStatus.find(status => status.headerProcessed === false);
    if ( ! stillWorking ) {
      this.passwords = [];
    }
  }

  success(index, message, event_type = EVENT_TYPES.DECRYPT_HEADER) {
    const type = event_type;
    const description = message;
    const detail = 'Filename was ' + this.filenames[index];
    this.eventsService.info({type, description, detail}).subscribe();
  }

  onkeydown(event: KeyboardEvent) {
    // only go on space or enter .
    if ( event['key'] === ' ' || event['key'] === 'Enter' ) {
      // get the input
      const target = event.currentTarget;
      const input = target['childNodes'][0];
      input.click();
    }
  }

  readFileContent(file: File, index): Promise<ArrayBuffer> {
    this.fileStatus[index].message = this.fMsgPreparing;
    return new Promise<ArrayBuffer>((resolve, reject) => {
      this.fileStatus[index].message = this.fMsgReading + ' ...';

        if (!file) {
            resolve(new ArrayBuffer(0));
        }

        const reader = new FileReader();

        reader.onload = (e) => {
            const buffer = reader.result as ArrayBuffer;

            this.files[index].progress = 100;
            this.fileStatus[index].message = this.fMsgLookingHeaders;
            resolve(buffer);
        };

        reader.onprogress = (event) => {
          let loaded = 0;
          if ( isNaN(event.loaded) ) {
            loaded = 0;
          } else {
            loaded = (event.loaded * 100) / file.size;
            if (isNaN(loaded)) {
              loaded = 0;
            }
          }
          if ( loaded > 100 ) {
            loaded = 100;
          }
          this.fileStatus[index].message = this.fMsgReading + ' ' + Math.trunc(loaded) + '%';
          this.files[index].progress += loaded;
        };

        reader.readAsArrayBuffer(file);
    });
  }

  clearFiles() {
    // reset everything!
    const fileDropRef = document.getElementById('fileDropRef') as HTMLInputElement;
    fileDropRef.value = '';
    this.files = [];
    this.filenames = [];
    this.fileStatus = [];
    this.fileContents = [];
    this.decryptedFile = [];
    this.ready = [];
    this.downloadsAvailable = false;
  }

  downloadAllFiles() {
    for (const i of Array(this.fileStatus.length).keys()) {
      if ( this.fileStatus[i].ready ) {
         this.downloadFile(i);
      }
    }
  }

  async decrypt (index: number, headerInfo: IEncryptionHeaderResponse) {
    this.processingStatus(index);

    const sliceStart = this.getHeaderSize(headerInfo);
    const encryptedContent = this.fileContents[index].slice(sliceStart);

    const key_buffer = base64ToArrayBuffer(headerInfo.symmetricKey);
    const iv_buffer = base64ToArrayBuffer(headerInfo.iv);
    const crypto_key = await importSecretKeyGCM(key_buffer);

    const decryptedContentPromise = decodeGSM(encryptedContent, iv_buffer, crypto_key);

    decryptedContentPromise.then( decryptedContent => {
      this.success(index, this.fMsgDecrypted, EVENT_TYPES.DECRYPT_FILE);
      this.processDecryptContent(index, decryptedContent);
    }, error => {
      this.failure(index, this.fMsgFailed, EVENT_TYPES.DECRYPT_FILE);
    });

  }

  private processingStatus(index: number) {
    this.fileStatus[index].message = this.fMsgProcessing;
    this.fileStatus[index].headerProcessed = true;
    this.verifyHeadersComplete();
  }

  processDecryptContent(index, decryptedContent) {
    this.decryptedFile[index] = decryptedContent;
    this.fileStatus[index].ready = true;
    this.ready[index] = true;
    this.fileStatus[index].message = this.fMsgDownloadReady;

    let hasDownload = false;
    let allFilesProcessed = true;
    // loop through file statues.  IF any of the files has not finished processing don't enable the download all
    // button.
    //
    // If all of the files are processed, only enable the download button if there is actually a download available.
    // This will allow download for only the decrypted files.
    for (const i of Array(this.fileStatus.length).keys()) {
      // if error and ready are both false, then the file processing has not completed.
      if ( this.fileStatus[i].error === false && this.fileStatus[i].ready === false ) {
        allFilesProcessed = false;
      } else if ( this.fileStatus[i].ready ) {
        hasDownload = true;
      }
    }

    if ( allFilesProcessed && hasDownload ) {
      this.downloadsAvailable = true;
    }
  }

  downloadFile(index: number) {
     if ( this.ready[index] ) {

       // just as a test, put the file contents into a buffer to download
       // if it doesn't have .d3e. call it filename.decrypted
       let newFilename = this.filenames[index];
       if ( newFilename.includes('.dd3e.') ) {
         newFilename = newFilename.replace('.d3e.', '.');
       }

       const blob = new Blob([this.decryptedFile[index]], {type: 'octet/stream'});
       pushFileToDesktop(blob, newFilename);
       const type = EVENT_TYPES.DECRYPT_FILE_DOWNLOAD;
       const description = 'Decrypted File was downloaded';
       const detail = 'Decrypted file downloaded as ' + newFilename + '. Decrypted from file ' + this.filenames[index];
       this.eventsService.info({type, description, detail}).subscribe();
     }
  }

  showHeaderInfo(fileIndex: number) {
    const filename = this.filenames[fileIndex];
    const headerInfo: IHeaderInfo = {
      filename: filename,
      sessionCount: 0,
      sessions: []};

    const slice = this.fileContents[fileIndex].slice(0, this.fileStatus[fileIndex].bytesRequired);
    if (slice.byteLength < this.fileStatus[fileIndex].bytesRequired) {
          const dialogRef = this.dialog.open(HeaderInfoComponent, {
            width: 'auto',
            height: 'auto',
            data: headerInfo
          });
          return;
    }
    const base64HeaderString = arrayBufferToBase64(slice);
    this.encryptionService.getSessionInfo(base64HeaderString).subscribe( (sessionResponse) => {
      headerInfo.sessionCount = sessionResponse.sessions.length;
      headerInfo.sessions = sessionResponse.sessions;
      const dialogRef = this.dialog.open(HeaderInfoComponent, {
        width: '500px',
        height: 'auto',
        data: headerInfo
      });
    },
        (error) => {
      const dialogRef = this.dialog.open(HeaderInfoComponent, {
        width: 'auto',
        height: 'auto',
        data: headerInfo
      });
    });

  }

}
